/**
 * Risk Assessment Static Data
 * Questions, entity types, and configuration constants for the C2 risk assessment.
 */

import type {
  C1Type,
  C1TypeId,
  C2Type,
  C2TypeId,
  Question,
  RiskConfig,
  RiskRating
} from '../types/risk-assessment.types';

// ─── C1 & C2 Entity Types ─────────────────────────────────────────────────────

export const C1_TYPES: C1Type[] = [
  {
    id: 'accountant',
    label: 'Accountant',
    icon: '🧮',
    desc: 'Professional services – accounting',
  },
  {
    id: 'legal',
    label: 'Legal Profession',
    icon: '⚖️',
    desc: 'Solicitors, barristers, legal practices',
  },
  {
    id: 'conveyancer',
    label: 'Conveyancer',
    icon: '🏠',
    desc: 'Conveyancing & property transfers',
  },
  {
    id: 'real_estate',
    label: 'Real Estate Agent',
    icon: '🏘️',
    desc: 'Real estate agents & agencies',
  },
  {
    id: 'bullion_dealer',
    label: 'Bullion Dealer',
    icon: '🥇',
    desc: 'Precious metals & bullion dealers',
  },
];

export const C2_TYPES: C2Type[] = [
  { id: 'individual', label: 'Individual', icon: '👤', hasOwnership: false },
  { id: 'sole_trader', label: 'Sole Trader', icon: '🧑‍💼', hasOwnership: false },
  {
    id: 'body_corporate',
    label: 'Body Corporate / Partnership / Association',
    icon: '🏢',
    hasOwnership: true,
  },
  { id: 'trust', label: 'Trust', icon: '📋', hasOwnership: true },
  {
    id: 'government',
    label: 'Government Body',
    icon: '🏛️',
    hasOwnership: true,
  },
];

// ─── Questions ─────────────────────────────────────────────────────────────────

/** Designated-service risk – Professional services (accountants, legal) */
export const DS_PROF: Question[] = [
  {
    id: 'ds1',
    text: "Does the client's request involve any high value transactions?",
    rating: 'Medium',
    category: 'Designated Service Risk',
    info: 'High value transactions include large settlements, share purchases, or financial arrangements above typical thresholds. AUSTRAC defines high value as AUD $10,000 or more for certain designated services.',
  },
  {
    id: 'ds2',
    text: "Does the client's request involve any physical cash, and if so, is this unusual in these circumstances?",
    rating: 'High',
    category: 'Designated Service Risk',
    info: 'Physical cash transactions are a key AML/CTF indicator. Even smaller cash amounts can elevate risk when combined with other factors. Consider whether cash use is typical for this client type and transaction.',
  },
  {
    id: 'ds3',
    text: "Does the client's request involve any virtual assets (such as digital currency, cryptocurrency, Bitcoin, Ethereum), and if so, is this unusual in these circumstances?",
    rating: 'High',
    category: 'Designated Service Risk',
    info: 'Virtual assets include Bitcoin, Ethereum, stablecoins, NFTs, and other digital tokens. Their pseudonymous nature creates higher ML/TF risk. Any virtual asset involvement should be scrutinised carefully.',
  },
  {
    id: 'ds4',
    text: 'Is the client requesting services which could help them create distance between themselves and their economic activity, making them effectively anonymous? (e.g. complex legal structures, nominee services hiding the true owner or controller)',
    rating: 'High',
    category: 'Designated Service Risk',
    info: 'Examples include layered company structures, nominee directorships, or nominee shareholdings designed to conceal the true beneficial owner. Legitimate clients generally have transparent ownership structures.',
  },
  {
    id: 'ds5',
    text: 'Has the client requested a service that has no apparent economic or legal purpose; would involve unusually complex or large transactions; or would involve an unusual pattern of transactions?',
    rating: 'High',
    category: 'Designated Service Risk',
    info: 'Consider whether the transaction has a plausible business or personal rationale. Transactions with no obvious purpose, unusual complexity, or patterns inconsistent with the client\'s profile are red flags.',
  },
];

/** Designated-service risk – Conveyancers / Real estate */
export const DS_CONV: Question[] = [
  {
    id: 'ds1',
    text: 'Is the real estate of high value ($1.5 million or more) and being purchased without a mortgage?',
    rating: 'Medium',
    category: 'Designated Service Risk',
    info: 'Properties valued at $1.5M or more purchased without a mortgage (i.e. cash purchase) are a key indicator per AUSTRAC real estate guidance. This threshold applies to the full property value, not just the cash portion.',
  },
  {
    id: 'ds2',
    text: 'Is the client paying wholly or partly with physical currency (i.e. physical notes or coins) valued at $50,000 or more?',
    rating: 'High',
    category: 'Designated Service Risk',
    info: 'Cash of $50,000 or more triggers specific AUSTRAC reporting obligations for real estate transactions. Even amounts below this threshold can be suspicious if unusual for the client or inconsistent with their profile.',
  },
  {
    id: 'ds3',
    text: 'Is the client paying wholly or partly with one or more virtual asset (such as digital currency, cryptocurrency, Bitcoin, Ethereum)?',
    rating: 'High',
    category: 'Designated Service Risk',
    info: 'Virtual assets used in real estate settlement are a significant AML/CTF risk indicator under the AUSTRAC Tranche 2 reforms. Any virtual asset payment, regardless of amount, requires enhanced scrutiny.',
  },
  {
    id: 'ds4',
    text: 'Has the client requested a service that has no apparent economic or legal purpose; would involve unusually complex or large transactions; or would involve an unusual pattern of transactions?',
    rating: 'High',
    category: 'Designated Service Risk',
    info: "Transactions with no plausible rationale, unusual complexity, or patterns inconsistent with the client's known profile warrant heightened scrutiny. Consider the client's stated purpose and whether it makes commercial sense.",
  },
];

/** Client risk – common to all entity types */
export const CR_COMMON: Question[] = [
  {
    id: 'cr1',
    text: 'Do you think the client may have engaged in criminal or unusual activity?',
    rating: 'High',
    category: 'Client Risk',
    note: 'If YES — notify your AML/CTF compliance officer via an Unusual Activity Report immediately.',
    info: "Indicators include unusual nervousness, inconsistent information across documents, known associations with criminal activity, reluctance to provide ID, or transactions inconsistent with the client's stated business or personal circumstances.",
  },
  {
    id: 'cr2',
    text: 'Do you think the client or a related party are or have previously been a domestic or international organisation politically exposed person (PEP)?',
    rating: 'Medium',
    category: 'Client Risk',
    info: 'A PEP is a person who holds or has held a prominent public position, such as a politician, senior government official, military officer, or senior executive of a state-owned enterprise. Related parties include close family members and known associates.',
  },
  {
    id: 'cr3',
    text: 'Do you think the client or a related party are or have previously been a foreign PEP?',
    rating: 'High',
    category: 'Client Risk',
    info: 'Foreign PEPs carry a higher risk rating than domestic PEPs due to reduced ability to verify their political exposure and increased corruption risk in certain jurisdictions. This includes PEPs from any country other than Australia.',
  },
  {
    id: 'cr4',
    text: "Is a third party acting on the client's behalf as their representative? (Select NO if the third party is a reporting entity enrolled with AUSTRAC and provides evidence of enrolment)",
    rating: 'Medium',
    category: 'Client Risk',
    info: 'A reporting entity enrolled with AUSTRAC (e.g. a solicitor or accountant acting in that capacity) reduces the risk. An unenrolled third-party representative increases risk as their own AML/CTF obligations may be unclear.',
  },
  {
    id: 'cr5',
    text: "Do you think the client has unexplained wealth disproportionate to their known sources of legitimate wealth? (e.g. occupation listed as 'student' or 'unemployed' but wanting to purchase high value real estate or business shares without evidence of funds)",
    rating: 'High',
    category: 'Client Risk',
    info: 'Unexplained wealth indicators include purchasing high-value assets inconsistent with stated occupation or income, large unexplained cash deposits, or reluctance to provide source-of-funds documentation when requested.',
  },
  {
    id: 'cr6',
    text: 'Is the client or a related party operating a charity or other non-profit organisation (NPO)?',
    rating: 'Medium',
    category: 'Client Risk',
    info: 'NPOs can be misused to move funds across borders or launder proceeds of crime. Verify the NPO\'s registration, stated charitable purpose, and funding sources. Check ACNC registration for Australian charities.',
  },
];

/** Client risk – ownership complexity (only for entities with ownership structures) */
export const CR_OWNERSHIP: Question = {
  id: 'cr_own',
  text: 'Does the client have an ownership or control structure that is unusually complex or not typical for this kind of entity?',
  rating: 'High',
  category: 'Client Risk',
  info: 'Complex ownership structures include multiple layers of holding companies, trusts within trusts, or arrangements where the ultimate beneficial owner (UBO) is difficult to identify. Legitimate entities generally have transparent and proportionate structures.',
};

/** Delivery channel risk */
export const DC_Q: Question[] = [
  {
    id: 'dc1',
    text: 'Will you be interacting with the client or their representative only through remote channels that prevent you from establishing that they are who they say they are? (e.g. online channels preventing you from linking their appearance to identity documents)',
    rating: 'High',
    category: 'Delivery Channel Risk',
    info: 'Remote channels include online-only interactions, email, or phone where identity cannot be visually confirmed. Face-to-face identity verification significantly reduces this risk. If remote only, consider using a certified digital ID verification service.',
  },
];

/** Country risk (parameterised by entity ownership) */
export const getCountryQ = (hasOwnership: boolean): Question[] => [
  {
    id: 'co1',
    text: 'Is the client or a related party resident of or located in a medium risk country? Do you think the client will transact with another person resident in one of these countries?',
    rating: hasOwnership ? 'High' : 'Medium',
    category: 'Country Risk',
    info: 'Medium risk countries are those identified by AUSTRAC, FATF, or other international bodies as having elevated ML/TF risk but not subject to enhanced countermeasures. Refer to the current AUSTRAC country risk guidance for the latest list.',
  },
  {
    id: 'co2',
    text: 'Do you have information suggesting the client or a related party is a resident of or located in a high risk country? Do you think the client will transact with another person resident in one of these countries?',
    rating: 'High',
    category: 'Country Risk',
    info: 'High risk countries include FATF black/grey list jurisdictions, those subject to UN sanctions, or countries with known systemic AML/CTF deficiencies. Transactions involving these countries require enhanced due diligence and may be prohibited.',
  },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

/** Build the full question set for a given C1 type and C2 entity type. */
export function buildQuestions(c1Id: C1TypeId, c2Type: C2Type): Question[] {
  const isConv = c1Id === 'conveyancer' || c1Id === 'real_estate';
  const hasOwn = c2Type?.hasOwnership || false;
  const ds = isConv ? DS_CONV : DS_PROF;
  const cr = hasOwn ? [...CR_COMMON, CR_OWNERSHIP] : CR_COMMON;
  return [...ds, ...cr, ...DC_Q, ...getCountryQ(hasOwn)];
}

/** Compute the overall risk rating from answered questions. */
export function computeRisk(
  questions: Question[],
  answers: Record<string, boolean>,
): RiskRating {
  let hi = 0;
  let med = 0;
  questions.forEach((q) => {
    if (answers[q.id] === true) {
      if (q.rating === 'High') hi++;
      else if (q.rating === 'Medium') med++;
    }
  });
  if (hi >= 1) return 'High';
  if (med >= 2) return 'Medium';
  return 'Low';
}

// ─── Visual Config ─────────────────────────────────────────────────────────────

export const RISK_CFG: Record<RiskRating, RiskConfig> = {
  High: {
    color: '#ef4444',
    bg: '#ef444415',
    border: '#ef444440',
    label: 'High Risk',
    cdd: 'Enhanced CDD (ECDD) Required',
    creditsMin: 5,
    creditsMax: 15,
    action:
      'Escalate to AML/CTF Compliance Officer immediately. Do NOT provide designated services until written approval is received.',
  },
  Medium: {
    color: '#f97316',
    bg: '#f9731615',
    border: '#f9731440',
    label: 'Medium Risk',
    cdd: 'Standard CDD + Enhanced Review',
    creditsMin: 3,
    creditsMax: 10,
    action:
      'Proceed with Standard CDD. Enhanced review may be required depending on specific risk factors identified.',
  },
  Low: {
    color: '#22c55e',
    bg: '#22c55e15',
    border: '#22c55e40',
    label: 'Low Risk',
    cdd: 'Standard CDD',
    creditsMin: 3,
    creditsMax: 5,
    action: 'Proceed with Standard CDD verification.',
  },
};

// ─── Indicative Credit Consumption (per entity type × risk level) ──────────────
// Source: AL-115 — ranges represent the indicative credits needed for full CDD.

export type CreditEntityCategory = 'individual' | 'pty_ltd' | 'government' | 'listed_company';

export const CREDIT_RANGES: Record<CreditEntityCategory, Record<RiskRating, { min: number; max: number; note?: string }>> = {
  individual: {
    Low: { min: 2, max: 3 },
    Medium: { min: 2, max: 3 },
    High: { min: 3, max: 5 },
  },
  pty_ltd: {
    Low: { min: 4, max: 6, note: 'Can vary depending on complexity' },
    Medium: { min: 4, max: 6, note: 'Can vary depending on complexity' },
    High: { min: 6, max: 10, note: 'Can vary depending on complexity' },
  },
  government: {
    Low: { min: 3, max: 5 },
    Medium: { min: 3, max: 5 },
    High: { min: 3, max: 5, note: 'High risk not typically applicable' },
  },
  listed_company: {
    Low: { min: 4, max: 6 },
    Medium: { min: 4, max: 6 },
    High: { min: 4, max: 6, note: 'High risk not typically applicable — exchange due diligence applies' },
  },
};

/** Map a C2TypeId to the credit entity category */
export function mapC2TypeToCategory(c2Type: C2TypeId | null | undefined): CreditEntityCategory {
  switch (c2Type) {
    case 'individual':
    case 'sole_trader':
      return 'individual';
    case 'government':
      return 'listed_company'; // Government bodies treated like listed companies for credit purposes
    case 'body_corporate':
    case 'trust':
    default:
      return 'pty_ltd';
  }
}

/** Get the indicative credit range for a given entity type and risk level */
export function getIndicativeCreditRange(
  c2Type: C2TypeId | null | undefined,
  risk: RiskRating,
): { min: number; max: number; note?: string } {
  const category = mapC2TypeToCategory(c2Type);
  return CREDIT_RANGES[category][risk];
}

export const CAT_COLOR: Record<string, string> = {
  'Designated Service Risk': '#818cf8',
  'Client Risk': '#f472b6',
  'Delivery Channel Risk': '#fb923c',
  'Country Risk': '#34d399',
};
